import { useEffect, useState } from 'react'
import { type FrameData } from '#app/types/capture.ts'

interface StaticResourcesError {
	message: string
	status: number
	statusText: string
	internal: boolean
	data: string
}

interface UseStaticFrameResult {
	frameData: FrameData | null
	loading: boolean
	error: StaticResourcesError | null
}

export function useStaticFrame(
	captureId: string,
	frameId: string,
): UseStaticFrameResult {
	const [frameData, setFrameData] = useState<FrameData | null>(null)
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState<StaticResourcesError | null>(null)

	useEffect(() => {
		const abortController = new AbortController()

		async function loadFrameData() {
			try {
				setLoading(true)
				setError(null)

				const frameUrl = `${ENV.STATIC_ASSET_URL}/${captureId}/${encodeURIComponent(frameId)}.json`

				const frameResponse = await fetch(frameUrl, {
					signal: abortController.signal,
				})

				if (abortController.signal.aborted) return

				if (!frameResponse.ok) {
					setError({
						message: `Failed to load frame data: ${frameResponse.status}`,
						status: frameResponse.status,
						statusText: frameResponse.statusText,
						internal: true,
						data: frameUrl,
					})
					return
				}

				const frame = await frameResponse.json()

				if (abortController.signal.aborted) return

				setFrameData(frame as FrameData)
			} catch (err) {
				if (abortController.signal.aborted) return
				setError({
					message:
						err instanceof Error ? err.message : 'Failed to load frame data',
					status: 500,
					statusText: 'Internal Server Error',
					internal: true,
					data: 'Failed to load frame data',
				})
				console.error('Failed to load frame data:', err)
			} finally {
				if (!abortController.signal.aborted) {
					setLoading(false)
				}
			}
		}

		void loadFrameData()

		return () => {
			abortController.abort()
		}
	}, [captureId, frameId])

	return {
		frameData,
		loading,
		error,
	}
}
